/**
 * Sanitizes a filename to remove problematic characters that can cause issues
 * with URL parsing, file retrieval, and storage paths.
 * 
 * This function:
 * - Replaces parentheses () with underscores
 * - Removes other special characters (except word chars, dots, hyphens, @)
 * - Normalizes multiple underscores to single underscores
 * - Removes leading/trailing underscores
 * 
 * @param fileName - The original filename to sanitize
 * @returns The sanitized filename safe for use in storage paths and URLs
 * 
 * @example
 * sanitizeFileName("My File (2024).pdf") // "My_File_2024_.pdf"
 * sanitizeFileName("Document (MRR).002463.v1.3.pdf") // "Document_MRR_.002463.v1.3.pdf"
 */
export function sanitizeFileName(fileName: string): string {
  if (!fileName || typeof fileName !== 'string') {
    return 'file';
  }

  const trimmed = fileName.trim();
  if (!trimmed) {
    return 'file';
  }

  return trimmed
    .replace(/[()]/g, '_') // Replace parentheses with underscores
    .replace(/[^\w.\-@]/g, '_') // Replace other special chars (except word chars, dots, hyphens, @)
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
}

/**
 * Sanitizes a storage path by sanitizing each segment
 * @param path - The storage path (e.g., "agreements/file.pdf" or "evidence/2024/document.pdf")
 * @returns The sanitized storage path
 */
export function sanitizeStoragePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return 'file';
  }

  return path
    .split('/')
    .map(segment => sanitizeFileName(segment))
    .join('/');
}


